import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SuperAdminRoute from '../components/Auth/SuperAdminRoute';
import { xeroAPI } from '../services/api';

// Utility function to format numbers with commas
const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

interface XeroStatus {
  isRunning: boolean;
  isScheduled: boolean;
  lastRun?: Date;
  nextRun?: Date;
  lastResult?: {
    success: boolean;
    message: string;
    duration: number;
    recordsProcessed?: {
      invoices: number;
      payments: number;
      bankTransactions: number;
      contacts: number;
      creditNotes: number;
      lineItems: number;
      accounts: number;
    };
    error?: string;
  };
}

interface TableInfo {
  table_name: string;
  column_count: number;
  estimated_rows: number;
}

interface TableData {
  data: any[];
  total: number;
  table: string;
  limit: number;
}

const Xero: React.FC = () => {
  const [status, setStatus] = useState<XeroStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [, setSelectedTable] = useState<string>('');
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchTables();
    
    // Set up periodic status refresh when monitor is running
    const interval = setInterval(() => {
      if (status?.isRunning || status?.isScheduled) {
        fetchStatus();
      }
    }, 5000); // Refresh every 5 seconds when running
    
    return () => clearInterval(interval);
  }, [status?.isRunning, status?.isScheduled]);

  const fetchStatus = async () => {
    try {
      const response = await xeroAPI.getStatus();
      setStatus(response);
    } catch (error) {
      console.error('Error fetching Xero status:', error);
      toast.error('Failed to fetch Xero status');
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await xeroAPI.getTables();
      setTables(response.tables);
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const handleStart = async () => {
    try {
      setRunning(true);
      await xeroAPI.start();
      toast.success('Xero monitor started successfully');
      await fetchStatus();
      await fetchTables();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to start Xero monitor');
    } finally {
      setRunning(false);
    }
  };

  const handleStop = async () => {
    try {
      setRunning(true);
      await xeroAPI.stop();
      toast.success('Xero monitor stopped successfully');
      fetchStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to stop Xero monitor');
    } finally {
      setRunning(false);
    }
  };

  const handleRunNow = async () => {
    try {
      setRunning(true);
      const response = await xeroAPI.run();
      const result = response;
      
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      
      fetchStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to run Xero data collection');
    } finally {
      setRunning(false);
    }
  };

  const handleViewLogs = async () => {
    try {
      const response = await xeroAPI.getLogs(100);
      setLogs(response.logs);
      setShowLogs(true);
    } catch (error) {
      toast.error('Failed to fetch logs');
    }
  };

  const handleViewTable = async (tableName: string) => {
    try {
      const response = await xeroAPI.getTableData(tableName, 10);
      setTableData(response);
      setSelectedTable(tableName);
    } catch (error) {
      toast.error('Failed to fetch table data');
    }
  };

  const handleExportTable = async (tableName: string) => {
    try {
      const response = await xeroAPI.exportTable(tableName);
      
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `xero_${tableName}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported ${tableName} data successfully`);
    } catch (error) {
      toast.error('Failed to export table data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalRecords = status?.lastResult?.recordsProcessed
    ? Object.values(status.lastResult.recordsProcessed).reduce((sum, count) => sum + count, 0)
    : 0;

  return (
    <SuperAdminRoute>
      <div className="space-y-8">
        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
          <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(167,139,250,0.28),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(56,189,248,0.18),transparent_55%)]" />
          <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
                <span className={`h-2 w-2 rounded-full ${status?.isScheduled ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                {status?.isScheduled ? 'Scheduled' : 'Not scheduled'}
              </div>
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Xero Monitor</h1>
              <p className="max-w-2xl text-white/70">Manage Xero accounting data extraction and monitoring.</p>
              <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <i className="fas fa-database text-white/60"></i>
                  {tables.length} tables
                </span>
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <i className="fas fa-clock text-white/60"></i>
                  Runs at 2 AM, 8 AM, 2 PM, 8 PM (CAT)
                </span>
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <i className="fas fa-dollar-sign text-white/60"></i>
                  Financial Data
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleRunNow}
                disabled={running}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-slate-900 hover:bg-slate-100 px-6 py-3 font-semibold transition-all shadow-lg disabled:opacity-50"
              >
                <i className="fas fa-play"></i>
                {running ? 'Running…' : 'Run now'}
              </button>
              <button
                onClick={handleViewLogs}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
              >
                <i className="fas fa-file-alt"></i>
                View logs
              </button>
            </div>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Monitor Status</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {status?.isScheduled ? (
                    <span className="text-green-600">Active</span>
                  ) : (
                    <span className="text-red-600">Stopped</span>
                  )}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {status?.isRunning ? 'Currently running' : 'Idle'}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <i className={`fas ${status?.isScheduled ? 'fa-play' : 'fa-stop'} text-purple-600 text-2xl`}></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Last Run</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {status?.lastRun ? (
                    new Date(status.lastRun).toLocaleDateString()
                  ) : (
                    <span className="text-gray-400">Never</span>
                  )}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {status?.lastResult?.success ? 'Successful' : status?.lastResult ? 'Failed' : 'N/A'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <i className="fas fa-history text-blue-600 text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Records Processed</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatNumber(totalRecords)}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  Last collection
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <i className="fas fa-chart-bar text-green-600 text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Data Tables</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(tables.length)}</p>
                <p className="text-gray-600 text-sm mt-1">
                  Available for export
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-xl">
                <i className="fas fa-table text-orange-600 text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Next Run</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {status?.nextRun ? (
                    new Date(status.nextRun).toLocaleDateString()
                  ) : (
                    <span className="text-gray-400">Not scheduled</span>
                  )}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {status?.nextRun ? (
                    new Date(status.nextRun).toLocaleTimeString()
                  ) : (
                    'No schedule'
                  )}
                </p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-xl">
                <i className="fas fa-clock text-indigo-600 text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">Xero Monitor Control</h3>
            <p className="text-sm text-gray-600 mt-1">Runs 4 times a day at 2 AM, 8 AM, 2 PM, and 8 PM CAT</p>
          </div>
          <div className="p-6">
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleStart}
                disabled={running || status?.isScheduled}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-play mr-2"></i>
                Start Monitor
              </button>
              <button
                onClick={handleStop}
                disabled={running || !status?.isScheduled}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-stop mr-2"></i>
                Stop Monitor
              </button>
              <button
                onClick={handleRunNow}
                disabled={running}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <i className="fas fa-sync mr-2"></i>
                Run Data Collection
              </button>
            </div>
          </div>
        </div>

        {/* Records Breakdown */}
        {status?.lastResult?.recordsProcessed && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Last Collection Breakdown</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(status.lastResult.recordsProcessed.invoices)}</p>
                  <p className="text-sm text-gray-600 mt-1">Invoices</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(status.lastResult.recordsProcessed.payments)}</p>
                  <p className="text-sm text-gray-600 mt-1">Payments</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(status.lastResult.recordsProcessed.bankTransactions)}</p>
                  <p className="text-sm text-gray-600 mt-1">Bank Transactions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(status.lastResult.recordsProcessed.contacts)}</p>
                  <p className="text-sm text-gray-600 mt-1">Contacts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(status.lastResult.recordsProcessed.creditNotes)}</p>
                  <p className="text-sm text-gray-600 mt-1">Credit Notes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(status.lastResult.recordsProcessed.lineItems)}</p>
                  <p className="text-sm text-gray-600 mt-1">Line Items</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(status.lastResult.recordsProcessed.accounts)}</p>
                  <p className="text-sm text-gray-600 mt-1">Accounts</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Tables */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">Data Tables</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Table Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Columns
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estimated Rows
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tables.map((table) => (
                  <tr key={table.table_name} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {table.table_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatNumber(table.column_count)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {table.estimated_rows ? formatNumber(table.estimated_rows) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleViewTable(table.table_name)}
                          className="text-indigo-600 hover:text-indigo-900 p-2 hover:bg-indigo-50 rounded-lg transition-all duration-200"
                          title="View Data"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          onClick={() => handleExportTable(table.table_name)}
                          className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition-all duration-200"
                          title="Export CSV"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Table Data Viewer */}
        {tableData && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  {tableData.table} Data ({tableData.total} total records)
                </h3>
                <button
                  onClick={() => setTableData(null)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {tableData.data.length > 0 && Object.keys(tableData.data[0]).map((key) => (
                      <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableData.data.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {Object.values(row).map((value, valueIndex) => (
                        <td key={valueIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Logs Modal */}
        {showLogs && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">Xero Monitor Logs</h3>
                  <button
                    onClick={() => setShowLogs(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                  {logs.map((log, index) => (
                    <div key={index} className="mb-1">{log}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </SuperAdminRoute>
  );
};

export default Xero;
